/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StaffMember } from "../shared/types";
import { localDate } from "../shared/dates";

type Props = { staff: StaffMember[]; defaultDue?: string | null; onAdded: () => void };

// Type a task and press Enter; due date, person and priority are optional.
export const QuickAddTask = defineComponent<Props>({
  name: "QuickAddTask",
  props: ["staff", "defaultDue", "onAdded"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const title = ref("");
    const due = ref<string>(props.defaultDue ?? localDate());
    const assignee = ref<number | null>(null);
    const high = ref(false);
    const saving = ref(false);

    const add = async () => {
      if (!title.value.trim() || saving.value) return;
      saving.value = true;
      try {
        await trpc.workday.tasks.create.mutate({
          title: title.value.trim(),
          due_date: due.value || null,
          assigned_to: assignee.value,
          priority: high.value ? "high" : "normal",
        });
        title.value = "";
        high.value = false;
        props.onAdded();
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <div class="flex flex-wrap items-center gap-2 rounded-xl border border-base-300 bg-base-100 p-2">
        <span class="material-symbols-outlined pl-1 text-base-content/40">add_task</span>
        <input
          class="min-w-48 flex-1 bg-transparent px-1 py-1 text-sm outline-none"
          placeholder={t("workday.tasks.add_placeholder")}
          v-model={title.value}
          onKeydown={(e: KeyboardEvent) => e.key === "Enter" && add()}
        />
        <input type="date" class="input input-ghost input-xs" title={t("workday.tasks.due")} v-model={due.value} />
        <select
          class="select select-ghost select-xs max-w-36"
          title={t("workday.tasks.assignee")}
          value={assignee.value ?? ""}
          onChange={(e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            assignee.value = v ? Number(v) : null;
          }}
        >
          <option value="">{t("workday.tasks.anyone")}</option>
          {props.staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <button
          class={["btn btn-xs btn-square", high.value ? "btn-error" : "btn-ghost"].join(" ")}
          title={t("workday.tasks.high_priority")}
          onClick={() => (high.value = !high.value)}
        >
          <span class="material-symbols-outlined text-base">priority_high</span>
        </button>
        <button class="btn btn-primary btn-xs" disabled={!title.value.trim() || saving.value} onClick={add}>
          {t("workday.tasks.add")}
        </button>
      </div>
    );
  },
});
